import { isEmpty } from 'lodash';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useSelectedOriAlias } from './use-selected-ori-alias';
import { useOriAliases } from '../../common/hooks/use-ori-aliases';

type Primitive = string | number | null | undefined;

// 'defaultValues' contains all fields from the form, even the field has empty/null values. Only fields in formBootstrapView will be included (guaranteed by `pruneDexFields`)
// Any field key that does not exist in 'defaultValues' will be ignored.
// 'formInitalValues': the initial value from form config, e.g. if the initial 'state' is NY, change ori alias will change the 'state' to it's initial value configured in dex form.
// 'existingValues': filled by users, keep whatever user has filled when switch ori alias.
function getPrefilledValues(
    defaultValues: Record<string, Primitive>,
    existingValues: Record<string, Primitive>,
    formInitalValues?: Record<string, Primitive>,
    useInitalValues?: boolean
) {
    return Object.keys(defaultValues ?? {}).reduce((acc, cur) => {
        if (useInitalValues && formInitalValues?.[cur]) {
            acc[cur] = formInitalValues?.[cur];
        } else if (existingValues?.[cur]) {
            acc[cur] = existingValues?.[cur];
        } else {
            acc[cur] = defaultValues[cur];
        }
        return acc;
    }, {} as Record<string, Primitive>);
}

type UseResetValuesOnOriAliasChangeParams = {
    form: UseFormReturn<any, any, undefined>;
    formInitalValues?: any;
    defaultValues?: any;
    prefilledEntityValues?: any;
};
export function useResetValuesAfterOriAliasChange({
    formInitalValues,
    form,
    defaultValues = {},
    prefilledEntityValues,
}: UseResetValuesOnOriAliasChangeParams) {
    const { multipleOriEnabled } = useEnabledFeatures();
    const {
        selectedOriAliasId,
        setSelectedOriAliasId,
        shouldResetInitialValues,
        setShouldResetInitialValues,
    } = useSelectedOriAlias();
    const oriAliasOptions = useOriAliases();

    useEffect(
        function initialSetup() {
            if (!multipleOriEnabled) {
                return;
            }
            if (!isEmpty(prefilledEntityValues)) {
                setShouldResetInitialValues(false);
            }

            return () => {
                setShouldResetInitialValues(true);
            };
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [] // only trigger initial setup once
    );

    useEffect(
        function resetFormValuesOnOriAliasChange() {
            if (!multipleOriEnabled || !oriAliasOptions.length) {
                return;
            }
            let oriAliasId = selectedOriAliasId;
            if (!selectedOriAliasId) {
                const defaultOriAliasId = oriAliasOptions.find((option) => option.isDefault)?.value;
                if (defaultOriAliasId) {
                    setSelectedOriAliasId(`${defaultOriAliasId}`);
                    oriAliasId = `${defaultOriAliasId}`;
                }
            }

            const existingValues = form.getValues() ?? {};

            const { params: existingParams, ...othersExistingValues } = existingValues;
            const { params: defaultParams, ...otherDefaultValues } = defaultValues;
            const shouldUseInitialValues =
                isEmpty(prefilledEntityValues) && shouldResetInitialValues;

            const prefilledParamsValues = getPrefilledValues(
                defaultParams,
                existingParams,
                formInitalValues,
                shouldUseInitialValues
            );
            const otherPrefilledValues = getPrefilledValues(
                otherDefaultValues,
                othersExistingValues
            );
            const resetValues = {
                ...otherPrefilledValues,
                params: prefilledParamsValues,
                oriAliasId,
            };
            form.reset(resetValues);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [multipleOriEnabled, selectedOriAliasId, oriAliasOptions, form]
    );
}
